import React, { useEffect, useRef, useState } from "react";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, Trash2, Power, Upload, Eye } from "lucide-react";

export default function AdminSignBanner() {
  const [banners, setBanners] = useState([]);
  const [image, setImage] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/login-banners").then((r) => setBanners(r.data.banners || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) return toast.error("Image too large — use under 2.5MB");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!image) return toast.error("Please choose an image first");
    setBusy(true);
    try {
      await api.post("/admin/login-banner", { image, enabled, start_at: startAt || null, end_at: endAt || null });
      toast.success("Banner uploaded");
      setImage(""); setStartAt(""); setEndAt(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); } finally { setBusy(false); }
  };

  const toggle = async (b) => { try { await api.put(`/admin/login-banner/${b.id}`, { enabled: !b.enabled }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const updateDates = async (b, s, e2) => { try { await api.put(`/admin/login-banner/${b.id}`, { start_at: s || null, end_at: e2 || null }); toast.success("Schedule updated"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const replaceImage = async (b, ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    if (f.size > 2_500_000) return toast.error("Image too large — use under 2.5MB");
    const reader = new FileReader();
    reader.onload = async () => { try { await api.put(`/admin/login-banner/${b.id}`, { image: reader.result }); toast.success("Image replaced"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
    reader.readAsDataURL(f);
  };
  const remove = async (b) => { if (!window.confirm("Delete this banner permanently?")) return; try { await api.delete(`/admin/login-banner/${b.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-[var(--gold)]/50";

  return (
    <div className="space-y-6" data-testid="admin-sign-banner">
      <PageTitle title="Sign Page Banner" subtitle="Upload a promotional popup shown on the Login & Register pages. Set an optional start/end date; change or delete anytime." />

      <GlassCard className="p-6">
        <h3 className="mb-3 flex items-center gap-2 font-semibold text-white"><ImagePlus size={16} className="text-[var(--gold)]" /> New Banner</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} data-testid="banner-file" className={inp} />
            <div className="mt-2 flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-white">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="banner-enabled" className="h-4 w-4 accent-emerald-500" /> Enabled
              </label>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div><label className="text-[11px] text-[var(--text-dim)]">Start date (optional)</label><input type="date" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="banner-start" className={inp} /></div>
              <div><label className="text-[11px] text-[var(--text-dim)]">End date (optional)</label><input type="date" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="banner-end" className={inp} /></div>
            </div>
            <LuxuryButton variant="gold" className="mt-3" disabled={busy || !image} onClick={upload} data-testid="banner-upload-btn"><Upload size={15} className="mr-1 inline" /> {busy ? "Uploading…" : "Upload Banner"}</LuxuryButton>
          </div>
          <div>
            <p className="mb-1 text-[11px] text-[var(--text-dim)]">Preview</p>
            <div className="grid min-h-[160px] place-items-center rounded-xl border border-dashed border-white/15 bg-black/30 p-3">
              {image ? <img src={image} alt="preview" className="max-h-64 w-auto rounded-lg" data-testid="banner-preview" /> : <span className="text-sm text-[var(--text-dim)]">No image selected</span>}
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="mb-3 font-semibold text-white">Existing Banners ({banners.length})</h3>
        {banners.length === 0 ? <p className="text-sm text-[var(--text-dim)]">No banners yet.</p> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {banners.map((b) => (
              <div key={b.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`banner-card-${b.id}`}>
                <img src={b.image} alt="banner" className="mb-2 h-36 w-full rounded-lg object-cover" />
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/50"}`}>{b.enabled ? "LIVE" : "OFF"}</span>
                  <span className="font-mono text-[10px] text-[var(--text-dim)]">{b.id}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <input type="date" defaultValue={b.start_at || ""} onBlur={(e) => updateDates(b, e.target.value, b.end_at)} className="rounded border border-white/10 bg-white/5 px-1.5 py-1 text-[10px] text-white" data-testid={`banner-start-${b.id}`} />
                  <input type="date" defaultValue={b.end_at || ""} onBlur={(e) => updateDates(b, b.start_at, e.target.value)} className="rounded border border-white/10 bg-white/5 px-1.5 py-1 text-[10px] text-white" data-testid={`banner-end-${b.id}`} />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => toggle(b)} data-testid={`banner-toggle-${b.id}`} className="flex items-center gap-1 rounded bg-white/10 px-2 py-1 text-[11px] font-bold text-white hover:bg-white/20"><Power size={12} /> {b.enabled ? "Turn Off" : "Turn On"}</button>
                  <label className="flex cursor-pointer items-center gap-1 rounded bg-white/10 px-2 py-1 text-[11px] font-bold text-white hover:bg-white/20"><Eye size={12} /> Replace<input type="file" accept="image/*" className="hidden" onChange={(e) => replaceImage(b, e)} data-testid={`banner-replace-${b.id}`} /></label>
                  <button onClick={() => remove(b)} data-testid={`banner-delete-${b.id}`} className="flex items-center gap-1 rounded bg-rose-500/20 px-2 py-1 text-[11px] font-bold text-rose-300 hover:bg-rose-500/30"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}
